/**
 * ProseMirrorRichTextField: Main component for the ProseMirror rich text editor
 *
 * This component provides a React interface for a ProseMirror editor. It:
 * 1. Creates a container div within the component
 * 2. Renders the editor UI through a React portal into that container
 * 3. Manages the editor lifecycle through ProseMirrorEditorInstance
 *
 *  Multiple instances can be used on the same page - each gets its own container.
 */

import { FC, useCallback, useContext, useState } from "react";
import type { Doc } from "sharedb/lib/client";

import type { OEmbedDataType, PathType, WorksheetType } from "../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../shared/worksheets/constants";
import { createBlockOfType } from "../../../../shared/worksheets/doc-helpers";
import { showOEmbedModal } from "../Embed/EmbedModal";

import { ProseMirrorPortal } from "./ProseMirrorPortal";
import type { ProseMirrorPortalProps } from "./prosemirror/types";

import { getPresenceViewTypeFromPageTabVariant } from "components/materials/page/helpers";
import { DocViewContext } from "providers/DocViewProvider";
import { useAppSelector } from "store/index";
import { selectCurrentUserId, selectSelectedPageTabVariant, userSelectors } from "store/selectors";
import { fetchOEmbed } from "utils/oembed";

import "prosemirror-menu/style/menu.css";
import "prosemirror-view/style/prosemirror.css";
import "./prosemirror/editor.css";

/**
 * Props for ProseMirrorRichTextField
 * Matches QuillRichTextField interface for consistency.
 */
export interface ProseMirrorRichTextFieldProps {
  /** Additional CSS classes for the editor wrapper */
  className?: string;

  /** ShareDB document */
  doc: Doc<WorksheetType>;

  /** Path to the field data */
  path: PathType;

  /** Path to the field itself; used for getting "maxLength" and other metadata */
  fieldPath?: PathType;

  /** Whether the editor is read-only */
  readOnly?: boolean;

  /** Whether the field is embedded in another component */
  embedded?: boolean;

  /** Event name for tracking */
  eventName?: string;

  /** Character counter mode */
  characterCounter: "NONE" | "READ" | "EDIT";

  /** Maximum length for the field */
  maxLength?: number | null;

  /** Whether to show a placeholder */
  showPlaceholder?: boolean;

  /** Placeholder text to display */
  placeHolderText?: string;

  /** Whether to offer embed on paste URL */
  offerEmbedOnPasteURL?: boolean;

  /** Initialize the last part of the path, if the data at doc[<path>] doesn't exist. */
  create?: boolean;
}

/**
 * ProseMirrorRichTextField creates a ProseMirror editor with React integration
 */
export const ProseMirrorRichTextField: FC<ProseMirrorRichTextFieldProps> = ({
  className = "",
  doc,
  path,
  fieldPath,
  readOnly: readOnlyProp,
  showPlaceholder = false,
  placeHolderText = "Type here…",
  eventName,
  embedded = false,
  create = false,
  maxLength = null,
  characterCounter = "NONE",
  offerEmbedOnPasteURL = false,
}) => {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const userId = useAppSelector(selectCurrentUserId);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const presenceViewType = getPresenceViewTypeFromPageTabVariant(tabVariant);
  const users = useAppSelector(userSelectors.selectEntities);
  const { readOnly: readOnlyCtx, pageId, isAuthoringPage } = useContext(DocViewContext);

  // If the caller passes readOnly as a prop, we use that; otherwise we use the context value.
  // This means that the prop can override the context, if necessary.
  const readOnly = typeof readOnlyProp === "boolean" ? readOnlyProp : readOnlyCtx;

  // Use a callback ref to set the container element when it's mounted
  const setContainer = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node);
  }, []);

  const addEmbedBlock = useCallback(
    (data: OEmbedDataType) => {
      const blockIndexPart = path[1];
      const newBlockIndex =
        typeof blockIndexPart === "number" ? blockIndexPart + 1 : parseInt(blockIndexPart, 10) + 1;
      const block = createBlockOfType("Embed");
      block.f[0] = { ...block.f[0], ...data };
      doc.submitOp([{ p: [BLOCKS_PATH, newBlockIndex], li: block }]);
    },
    [doc, path],
  );

  const onEmbedURL = useCallback(
    async (url: string) => {
      const { embed: oEmbedData } = await fetchOEmbed(url);
      if (oEmbedData) {
        showOEmbedModal(url, addEmbedBlock, true);
      }
    },
    [addEmbedBlock],
  );

  const fieldClassName = className || "field-ProseMirrorRichTextField";

  const portalProps: ProseMirrorPortalProps = {
    className: fieldClassName,
    doc,
    path,
    fieldPath,
    placeHolderText,
    readOnly,
    containerElement,
    showPlaceholder,
    userId: userId ?? undefined,
    presenceViewType,
    users,
    eventName,
    pageId,
    embedded,
    create,
    maxLength,
    characterCounter,
    isAuthoringPage,
    onEmbedURLFromPaste: offerEmbedOnPasteURL ? onEmbedURL : undefined,
    onEmbedURLFromLink: offerEmbedOnPasteURL && path[0] === BLOCKS_PATH ? onEmbedURL : undefined,
  };

  return (
    <div className={fieldClassName} ref={setContainer}>
      {containerElement && <ProseMirrorPortal {...portalProps} />}
    </div>
  );
};
